import { useEffect, useState } from 'react';
import { RESOURCE_ERROR_MESSAGES } from './constants';

/**
 * Fetches a generated resource details by ID.
 * @param {Object} params
 * @param {() => Promise<Object>} params.fetch Resolves to the loaded resource
 * @param {string} params.resourceLabel  Resource label used in the error message, e.g. "flashcard deck"
 * @param {string} params.id The resource's ID
 * @returns {Object} resource, setResource, isLoading, loadError
 */
const useResourceDetail = ({ fetch, resourceLabel, id }) => {
  const [resource, setResource] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    // Clears the previous id's resource immediately rather than leaving it in place until the new fetch resolves
    setResource(null);

    // Guards against a fetch for a previous id resolving after a newer one
    // has already started (e.g. the user navigates from deck A to deck B
    // before A's request finishes). Without it, A's stale response could
    // land after B's and overwrite B's state. Also prevents setting state
    // after this effect's own cleanup has run, i.e. after unmount.
    let isCurrent = true;

    const load = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const data = await fetch();
        if (isCurrent) {
          if (data) {
            setResource(data);
          } else {
            setLoadError(`Unable to load this ${resourceLabel}.`);
          }
        }
      } catch (err) {
        console.log(err);
        if (isCurrent) {
          setLoadError(
            RESOURCE_ERROR_MESSAGES[err?.code]?.(resourceLabel) ??
              `Unable to load this ${resourceLabel}.`,
          );
        }
      } finally {
        if (isCurrent) setIsLoading(false);
      }
    };
    load();

    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return { resource, setResource, isLoading, loadError };
};

export default useResourceDetail;
